import { Box } from "@mui/material";
import {
  useCallback,
  type FocusEvent,
  type JSX,
  type MouseEventHandler,
  type PointerEventHandler,
} from "react";
import type { RowRendererProps } from "react-arborist";

export type TreeRowProps<T = unknown> = RowRendererProps<T> & {
  onClick?: MouseEventHandler<HTMLDivElement>;
  onPointerEnter?: PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: PointerEventHandler<HTMLDivElement>;
};

/**
 *  @returns A row component for the tree view.
 */
export function TreeRow<T = unknown>({
  innerRef,
  attrs,
  children,
  onClick,
  onPointerEnter,
  onPointerLeave,
}: TreeRowProps<T>): JSX.Element {
  // This is mimicking what react-arborist does for the RowContainer
  // https://github.com/jameskerr/react-arborist/blob/main/modules/react-arborist/src/components/default-row.tsx#L15
  // Stopping the propagation prevents the tree elements to get focus when an element in a dialog
  // opened by an element in the tree should have it
  const onFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <Box
      component="div"
      ref={innerRef}
      {...attrs}
      onClick={onClick}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocus={onFocus}
      // The browser may decide to show something more prominent when an element has the focus
      // We explicitly disable any style in such cases for the element in the tree
      // https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Selectors/:focus-visible
      sx={{
        ":focus-visible": {
          outline: "none",
          border: "none",
        },
      }}
    >
      {children}
    </Box>
  );
}
